import React, { useState, useMemo, useCallback } from 'react';
import { formatNumber } from '../../utils/formatHelpers';

/* The rest of the app renders weight in kg (see formatWeight in formatHelpers).
   Kept as one constant so the unit can be switched in a single place. */
const WEIGHT_UNIT = 'kg';

const COLUMNS = [
  { key: 'part_number',  label: 'Part #',                  numeric: false },
  { key: 'item_name',    label: 'Item',                    numeric: false },
  { key: 'total_cases',  label: 'Cases',                   numeric: true  },
  { key: 'total_units',  label: 'Units',                   numeric: true  },
  { key: 'total_weight', label: `Weight (${WEIGHT_UNIT})`, numeric: true  },
  { key: 'pallet_count', label: 'Pallets',                 numeric: true  },
];

const fmtWeight = (value) => {
  const n = parseFloat(value);
  if (isNaN(n)) return '0.00';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const Caret = ({ dir }) => (
  <svg className="th-sort__caret" width="10" height="10" viewBox="0 0 24 24"
       fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
    {dir === 'asc' ? <polyline points="6 15 12 9 18 15" /> : <polyline points="6 9 12 15 18 9" />}
  </svg>
);

/* ─── Totals strip — sits above the chart ──────────────────────────── */
export const ItemsTotals = ({ totals, loading }) => {
  const t = totals || {};
  return (
    <div className={`items-totals ${loading ? 'items-totals--skeleton' : ''}`}>
      <div className="items-totals__cell">
        <span className="items-totals__value">{formatNumber(t.item_count)}</span>
        <span className="items-totals__label">Items</span>
      </div>
      <div className="items-totals__cell">
        <span className="items-totals__value">{formatNumber(t.total_cases)}</span>
        <span className="items-totals__label">Cases</span>
      </div>
      <div className="items-totals__cell">
        <span className="items-totals__value">{formatNumber(t.total_units)}</span>
        <span className="items-totals__label">Units</span>
      </div>
      <div className="items-totals__cell">
        <span className="items-totals__value">{fmtWeight(t.total_weight)}</span>
        <span className="items-totals__label">Weight ({WEIGHT_UNIT})</span>
      </div>
    </div>
  );
};

/* ─── Full sortable item table ─────────────────────────────────────── */
const ItemsTable = ({ rows, totals, loading, onItemClick, onExport }) => {
  const [sort, setSort] = useState({ key: 'total_cases', dir: 'desc' });

  const handleSort = useCallback((col) => {
    setSort(prev => prev.key === col.key
      ? { key: col.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key: col.key, dir: col.numeric ? 'desc' : 'asc' });
  }, []);

  const sorted = useMemo(() => {
    const list = Array.isArray(rows) ? [...rows] : [];
    const col = COLUMNS.find(c => c.key === sort.key) || COLUMNS[2];
    const factor = sort.dir === 'asc' ? 1 : -1;
    return list.sort((a, b) => {
      if (col.numeric) {
        return ((parseFloat(a[col.key]) || 0) - (parseFloat(b[col.key]) || 0)) * factor;
      }
      return String(a[col.key] || '').localeCompare(String(b[col.key] || ''),
        'en', { numeric: true, sensitivity: 'base' }) * factor;
    });
  }, [rows, sort]);

  if (loading) return <div className="chart-loading">Loading item data...</div>;

  const t = totals || {};
  const isEmpty = sorted.length === 0;

  return (
    <div className="items-table">
      <div className="items-table__bar">
        <button className="export-btn" onClick={onExport} disabled={isEmpty}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Export CSV
        </button>
      </div>

      {isEmpty ? (
        <div className="chart-empty">No items in this range.</div>
      ) : (
        <div className="items-table__scroll">
          <table className="results-table results-table--analytics">
            <thead>
              <tr>
                {COLUMNS.map(col => {
                  const active = sort.key === col.key;
                  return (
                    <th
                      key={col.key}
                      className={col.numeric ? 'num' : ''}
                      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      <button
                        type="button"
                        className={`th-sort ${col.numeric ? 'th-sort--num' : ''} ${active ? 'th-sort--active' : ''}`}
                        onClick={() => handleSort(col)}
                      >
                        {col.label}
                        <Caret dir={active ? sort.dir : 'desc'} />
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {sorted.map(row => (
                <tr
                  key={row.item_id}
                  onClick={() => onItemClick && onItemClick(row)}
                  onKeyDown={e => e.key === 'Enter' && onItemClick && onItemClick(row)}
                  tabIndex={0}
                  aria-label={`${row.item_name}: ${row.total_cases} cases. Open details.`}
                >
                  <td className="part-number">{row.part_number}</td>
                  <td>{row.item_name}</td>
                  <td className="num">{formatNumber(row.total_cases)}</td>
                  <td className="num">{formatNumber(row.total_units)}</td>
                  <td className="num">{fmtWeight(row.total_weight)}</td>
                  <td className="num">{formatNumber(row.pallet_count)}</td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr>
                <td className="items-table__total-label">Total</td>
                <td>{formatNumber(t.item_count)} items</td>
                <td className="num">{formatNumber(t.total_cases)}</td>
                <td className="num">{formatNumber(t.total_units)}</td>
                <td className="num">{fmtWeight(t.total_weight)}</td>
                <td className="num">&mdash;</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default ItemsTable;
